import { lazy, Suspense, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import {
  landingPathFor,
  requestedReturnPath,
} from '../features/auth/postLoginPath'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { RouteErrorBoundary } from '../components/ErrorBoundary'
import RouteHead from '../seo/RouteHead'
import {
  AREA_SLUGS,
  CAMPUS_SLUGS,
  GUIDES_PATH,
  HUB_PATH,
  LANDLORD_GUIDE_SLUGS,
  areaPath,
  campusPath,
  guidePath,
} from '../seo/contentPaths'

// Lazy-loaded feature components
const BrowseView = lazy(() => import('../features/listings/BrowseView'))
const PropertyDetail = lazy(() => import('../features/listings/PropertyDetail'))
const SavedListings = lazy(() => import('../features/listings/SavedListings'))
const MessagesView = lazy(() => import('../features/messaging/MessagesView'))
const ConversationView = lazy(
  () => import('../features/messaging/ConversationView')
)
const ProfileView = lazy(() => import('../features/profile/ProfileView'))
const EditProfile = lazy(() => import('../features/profile/EditProfile'))
const StudentTenantDashboard = lazy(
  () => import('../features/profile/StudentTenantDashboard')
)
const PaymentsView = lazy(() => import('../features/payments/PaymentsView'))
const ApplicationFlow = lazy(
  () => import('../features/applications/ApplicationFlow')
)
const MyApplications = lazy(
  () => import('../features/applications/MyApplications')
)
const RentalApplicationPage = lazy(
  () => import('../features/applications/RentalApplicationPage')
)
const AgreementView = lazy(
  () => import('../features/applications/AgreementView')
)

// Housemates (available to all authenticated users)
const HousematesHub = lazy(() => import('../features/housemates/HousematesHub'))
const MyHousemateProfile = lazy(
  () => import('../features/housemates/MyHousemateProfile')
)
const StartHousehold = lazy(
  () => import('../features/housemates/StartHousehold')
)
const HousemateProfilePage = lazy(
  () => import('../features/housemates/HousemateProfilePage')
)

// Auth pages
const LoginPage = lazy(() => import('../features/auth/LoginPage'))
const RegisterPage = lazy(() => import('../features/auth/RegisterPage'))
const ForgotPasswordPage = lazy(
  () => import('../features/auth/ForgotPasswordPage')
)
const ResetPasswordPage = lazy(
  () => import('../features/auth/ResetPasswordPage')
)
const VerifyEmailPage = lazy(() => import('../features/auth/VerifyEmailPage'))
const CosignerAcceptPage = lazy(
  () => import('../features/cosigner/CosignerAcceptPage')
)
const CosignerDashboard = lazy(
  () => import('../features/cosigner/CosignerDashboard')
)
const ShowingCancel = lazy(() => import('../features/listings/ShowingCancel'))
const GuarantySign = lazy(() => import('../features/cosigner/GuarantySign'))
const BankHolderAuthorize = lazy(
  () => import('../features/payments/BankHolderAuthorize')
)
const TenantInviteAccept = lazy(
  () => import('../features/auth/TenantInviteAccept')
)
const LeadInviteAccept = lazy(() => import('../features/auth/LeadInviteAccept'))
const HouseholdInviteAccept = lazy(
  () => import('../features/groups/HouseholdInviteAccept')
)
const WaitlistStop = lazy(() => import('../features/housemates/WaitlistStop'))
const WaitlistJoin = lazy(() => import('../features/housemates/WaitlistJoin'))
const CohortPage = lazy(() => import('../features/housemates/CohortPage'))
const PayLinkOpen = lazy(() => import('../features/utilities/PayLinkOpen'))
const LandlordInviteAccept = lazy(
  () => import('../features/auth/LandlordInviteAccept')
)

// Legal pages (public)
const TermsPage = lazy(() =>
  import('../features/legal/LegalPages').then(m => ({ default: m.TermsPage }))
)
const PrivacyPage = lazy(() =>
  import('../features/legal/LegalPages').then(m => ({ default: m.PrivacyPage }))
)
const LegalHub = lazy(() =>
  import('../features/legal/LegalPages').then(m => ({ default: m.LegalHub }))
)
const EsignPage = lazy(() =>
  import('../features/legal/LegalPages').then(m => ({ default: m.EsignPage }))
)
const ScreeningPage = lazy(() =>
  import('../features/legal/LegalPages').then(m => ({
    default: m.ScreeningPage,
  }))
)
const FeesPage = lazy(() =>
  import('../features/legal/LegalPages').then(m => ({ default: m.FeesPage }))
)
const TenantRightsPage = lazy(() =>
  import('../features/legal/LegalPages').then(m => ({
    default: m.TenantRightsPage,
  }))
)
const CommunityPage = lazy(() =>
  import('../features/legal/LegalPages').then(m => ({
    default: m.CommunityPage,
  }))
)

// Public content pages: neighborhoods, off-campus pages, guides
const GuidesIndex = lazy(() => import('../features/guides/GuidesIndex'))
const AreaPage = lazy(() => import('../features/guides/AreaPage'))
const CampusPage = lazy(() => import('../features/guides/CampusPage'))
const LandlordGuide = lazy(() => import('../features/guides/LandlordGuide'))
const HubGuide = lazy(
  () => import('../features/guides/hub/StudentHousingGuide')
)

// Owner-specific pages
const OwnerDashboard = lazy(() => import('../features/owner/OwnerDashboard'))
const LandlordInbox = lazy(() => import('../features/owner/LandlordInbox'))
const PropertyWorkspace = lazy(
  () => import('../features/owner/PropertyWorkspace')
)
const OnboardTenantsFlow = lazy(
  () => import('../features/owner/property/OnboardTenantsFlow')
)
const LandlordListingForm = lazy(
  () => import('../features/owner/LandlordListingForm')
)
const RentedPropertyForm = lazy(
  () => import('../features/owner/RentedPropertyForm')
)
const ImportProperties = lazy(
  () => import('../features/owner/ImportProperties')
)
const RentCollection = lazy(() => import('../features/owner/RentCollection'))
const PaymentsIn = lazy(() => import('../features/owner/PaymentsIn'))
const SecurityDeposits = lazy(
  () => import('../features/owner/SecurityDeposits')
)
const Bookkeeping = lazy(() => import('../features/owner/Bookkeeping'))
const TaxCenter = lazy(() => import('../features/owner/TaxCenter'))
const OwnerDocuments = lazy(() => import('../features/owner/Documents'))
const OwnerTeam = lazy(() => import('../features/owner/Team'))
const TeamInviteAccept = lazy(() => import('../features/auth/TeamInviteAccept'))
const OwnerReports = lazy(() => import('../features/owner/Reports'))
const OwnerShowings = lazy(() => import('../features/owner/Showings'))
const GoogleCalendarCallback = lazy(
  () => import('../features/owner/GoogleCalendarCallback')
)
const OwnerAutomation = lazy(() => import('../features/owner/Automation'))
const OwnerVendors = lazy(() => import('../features/owner/OwnerVendors'))
const OwnerInspections = lazy(() => import('../features/owner/Inspections'))
const InspectionEditor = lazy(
  () => import('../features/owner/property/InspectionEditor')
)
const LeaseTemplates = lazy(
  () => import('../features/owner/leases/LeaseTemplates')
)
const LeaseTemplateEditor = lazy(
  () => import('../features/owner/leases/LeaseTemplateEditor')
)
const InspectionReport = lazy(
  () => import('../features/inspections/InspectionReport')
)
// Not-yet-real owner screens are gated behind an honest Coming Soon page
// (launch plan P0-3). Their mock components stay in the repo as design
// references but are intentionally unrouted.
const ComingSoon = lazy(() => import('../components/common/ComingSoon'))
// Tenant screening (Checkr). Hidden in the UI while the server reports
// screening as not enabled.
const ApplicationRecord = lazy(
  () => import('../features/owner/ApplicationRecord')
)
const LandlordScreening = lazy(
  () => import('../features/screening/LandlordScreening')
)
const MyScreening = lazy(() => import('../features/screening/MyScreening'))
const SelfScreeningOrder = lazy(
  () => import('../features/screening/SelfScreeningOrder')
)
const ScreeningConsent = lazy(
  () => import('../features/screening/ScreeningConsent')
)

// Households (student only; Groups merged into Housemates 2026-10-26)
const GroupChat = lazy(() => import('../features/groups/GroupChat'))
const HouseholdPage = lazy(() => import('../features/groups/HouseholdPage'))
const HouseholdPublic = lazy(() => import('../features/groups/HouseholdPublic'))

// Landing page
const LandingPage = lazy(() => import('../features/landing/LandingPage'))

// Pre-qualification
const PreQualificationFlow = lazy(
  () => import('../features/applications/PreQualificationFlow')
)

// Layout wrapper
const AppLayout = lazy(() => import('../components/layout/AppLayout'))
const NotFound = lazy(() => import('../components/common/NotFound'))
// The landlord tools strip loads with the first landlord page, not with the
// app shell every visitor downloads.
const LazyOwnerToolsLayout = lazy(
  () => import('../features/owner/OwnerToolsLayout')
)

function OwnerToolsLayout({ children }) {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <LazyOwnerToolsLayout>{children}</LazyOwnerToolsLayout>
    </Suspense>
  )
}

OwnerToolsLayout.propTypes = { children: PropTypes.node }

/**
 * Smart home page — shows landing page for guests, browse for tenants,
 * and redirects landlords to their dashboard
 */
function HomePage() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return <SuspenseFallback />
  }

  if (user?.userType === 'owner') {
    return <Navigate to="/dashboard" replace />
  }

  if (user?.userType === 'cosigner') {
    return <Navigate to="/cosigner" replace />
  }

  if (user) {
    return (
      <Suspense fallback={<SuspenseFallback />}>
        <BrowseView />
      </Suspense>
    )
  }

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <LandingPage />
    </Suspense>
  )
}

/**
 * The old Groups pages live on as redirects to the one household page
 * (`/groups/:id` -> `/households/:id`, `/groups/:id/chat` -> its chat).
 */
function GroupRedirect({ suffix = '' }) {
  const { id } = useParams()
  return <Navigate to={`/households/${id}${suffix}`} replace />
}

GroupRedirect.propTypes = { suffix: PropTypes.string }

/**
 * /households/:id: the household page for a signed-in renter; for someone
 * signed out (a shared link) the public card when every member opted in,
 * with the waitlist, else a sign-in line. Landlords go to their dashboard.
 */
function HouseholdRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <SuspenseFallback />
  if (!user) {
    return (
      <Suspense fallback={<SuspenseFallback />}>
        <HouseholdPublic />
      </Suspense>
    )
  }
  return (
    <RoleRoute allowedRoles={['student']}>
      <Suspense fallback={<SuspenseFallback />}>
        <HouseholdPage />
      </Suspense>
    </RoleRoute>
  )
}

/**
 * Loading fallback component for Suspense
 */
function SuspenseFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}

/**
 * Protected route wrapper - requires authentication
 */
function ProtectedRoute({ children }) {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <SuspenseFallback />
  }

  // Keep the destination through sign-in: the login page returns here.
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return children || <Outlet />
}

/**
 * Role-based route wrapper - requires specific user type
 */
function RoleRoute({ allowedRoles, children }) {
  const { user, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <SuspenseFallback />
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  if (!allowedRoles.includes(user.userType)) {
    // Redirect to appropriate home based on role
    const redirectPath = user.userType === 'owner' ? '/dashboard' : '/'
    return <Navigate to={redirectPath} replace />
  }

  return children || <Outlet />
}

/**
 * Sends a signed-in user on from the sign-in pages: to the page they were
 * sent here from (`state.from` or `?next=`, same-origin paths only), else
 * to their landing page (landlords their dashboard, tenants with a lease
 * theirs, everyone else home).
 */
function SignedInRedirect({ user }) {
  const location = useLocation()
  const requested = requestedReturnPath(location)
  const [landing, setLanding] = useState(null)

  useEffect(() => {
    if (requested) return undefined
    let active = true
    landingPathFor(user).then(path => {
      if (active) setLanding(path)
    })
    return () => {
      active = false
    }
  }, [user, requested])

  const target = requested || landing
  if (!target) return <SuspenseFallback />
  return <Navigate to={target} replace />
}

SignedInRedirect.propTypes = {
  user: PropTypes.shape({ userType: PropTypes.string }).isRequired,
}

/**
 * Public route wrapper - redirects authenticated users
 */
function PublicRoute({ children }) {
  const { user, isLoading } = useAuth()
  // Only the first session check blanks the page. A sign-in in progress
  // also sets isLoading, and the form has to stay mounted through it (its
  // fields survive a wrong password, and its button shows the spinner).
  const [sessionChecked, setSessionChecked] = useState(!isLoading)
  if (!isLoading && !sessionChecked) setSessionChecked(true)

  if (isLoading && !sessionChecked) {
    return <SuspenseFallback />
  }

  if (user) {
    return <SignedInRedirect user={user} />
  }

  return children || <Outlet />
}

/**
 * Route configuration
 */
const routeConfig = [
  // Auth pages (redirect if already logged in)
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Suspense fallback={<SuspenseFallback />}>
          <LoginPage />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/register',
    element: (
      <PublicRoute>
        <Suspense fallback={<SuspenseFallback />}>
          <RegisterPage />
        </Suspense>
      </PublicRoute>
    ),
  },

  // Legal pages (public — the privacy URL is required for Plaid production)
  {
    path: '/terms',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <TermsPage />
      </Suspense>
    ),
  },
  {
    path: '/privacy',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <PrivacyPage />
      </Suspense>
    ),
  },
  {
    path: '/legal',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <LegalHub />
      </Suspense>
    ),
  },
  {
    path: '/legal/esign',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <EsignPage />
      </Suspense>
    ),
  },
  {
    path: '/legal/screening',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ScreeningPage />
      </Suspense>
    ),
  },
  {
    path: '/legal/fees',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <FeesPage />
      </Suspense>
    ),
  },
  {
    path: '/legal/tenant-rights',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <TenantRightsPage />
      </Suspense>
    ),
  },
  {
    path: '/legal/community',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <CommunityPage />
      </Suspense>
    ),
  },

  // Password reset and email verification (public; reached from emails)
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ResetPasswordPage />
      </Suspense>
    ),
  },
  {
    path: '/verify-email',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <VerifyEmailPage />
      </Suspense>
    ),
  },

  // Cosigner invitation acceptance (public — reached from the email link)
  {
    path: '/cosigner/accept/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <CosignerAcceptPage />
      </Suspense>
    ),
  },
  // Existing-tenant invitation (public — reached from the email link)
  {
    path: '/tenant-invite/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <TenantInviteAccept />
      </Suspense>
    ),
  },
  // Lead invite (public — reached from the auto-reply to a Zillow,
  // Apartments.com or other-site inquiry)
  {
    path: '/lead/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <LeadInviteAccept />
      </Suspense>
    ),
  },
  // Household invite (public — a household member's invite link; renders
  // without a session, noindexed by the /household prefix)
  {
    path: '/household/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <HouseholdInviteAccept />
      </Suspense>
    ),
  },
  // Short pay link from a shared-bill notice (public, by token): records
  // the tap as sent, then opens Venmo, PayPal or Cash App. Noindexed by
  // the /pay prefix.
  {
    path: '/pay/:token/:method',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <PayLinkOpen />
      </Suspense>
    ),
  },
  // The Housemates waitlist email's stop link (public, no account)
  {
    path: '/housemates/waitlist/stop/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <WaitlistStop />
      </Suspense>
    ),
  },
  // The waitlist's "your cohort is open" link (public, by token; noindexed
  // by the /housemates prefix): makes or links the account, then the quiz.
  {
    path: '/housemates/join/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <WaitlistJoin />
      </Suspense>
    ),
  },
  // Landlord invite (public — the admin-issued link that creates a
  // landlord account while sign-ups are closed)
  {
    path: '/landlord-invite/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <LandlordInviteAccept />
      </Suspense>
    ),
  },
  // A bank account holder signs their own debit authorization (public,
  // by token from the email a tenant asked Rentra to send; noindexed by
  // the /bank-authorization prefix)
  {
    path: '/bank-authorization/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <BankHolderAuthorize />
      </Suspense>
    ),
  },
  // Guarantor signing (public — reached from the guaranty email link)
  {
    path: '/guaranty/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <GuarantySign />
      </Suspense>
    ),
  },
  // Tour cancellation (public — reached from the confirmation email)
  {
    path: '/showings/cancel/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <ShowingCancel />
      </Suspense>
    ),
  },
  // Team invitation (public page; creates the landlord account or accepts
  // as the signed-in one)
  {
    path: '/team-invite/:token',
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <TeamInviteAccept />
      </Suspense>
    ),
  },

  // Main app layout — accessible to guests and authenticated users
  {
    element: (
      <Suspense fallback={<SuspenseFallback />}>
        <AppLayout />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      // Home — smart routing for guests, tenants, and landlords
      {
        path: '/',
        element: <HomePage />,
      },
      {
        path: '/listings',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <BrowseView />
          </Suspense>
        ),
      },
      // The public Housemates cohort page, /housemates/<city>/<area>/<YYYY-MM>:
      // counts and opted-in cards, rendered without a session and indexable
      // (INDEXABLE_PATTERNS in seo/routes.js).
      {
        path: '/housemates/:city/:area/:month',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <CohortPage />
          </Suspense>
        ),
      },
      // Guarantor landing page. Any signed-in user can cosign (a parent may
      // already be a landlord or tenant), so this is role-agnostic.
      {
        path: '/cosigner',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <CosignerDashboard />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // A co-signer's own rental application (guarantor questions only).
      {
        path: '/cosigner/rental-application',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <RentalApplicationPage mode="guarantor" />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/favorites',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <SavedListings />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/listings/:id',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <PropertyDetail />
          </Suspense>
        ),
      },

      // Public content pages (seo/contentPaths.js): one explicit route per
      // page, so an unknown slug is a real 404 at the edge too.
      ...[
        [GUIDES_PATH, <GuidesIndex key="index" />],
        [HUB_PATH, <HubGuide key="hub" />],
        ...LANDLORD_GUIDE_SLUGS.map(slug => [
          guidePath(slug),
          <LandlordGuide key={slug} slug={slug} />,
        ]),
        ...AREA_SLUGS.map(slug => [
          areaPath(slug),
          <AreaPage key={slug} slug={slug} />,
        ]),
        ...CAMPUS_SLUGS.map(slug => [
          campusPath(slug),
          <CampusPage key={slug} slug={slug} />,
        ]),
      ].map(([path, page]) => ({
        path,
        element: <Suspense fallback={<SuspenseFallback />}>{page}</Suspense>,
      })),

      // Messaging routes (auth required)
      {
        path: '/messages',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <MessagesView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/messages/:conversationId',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <ConversationView />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      // Housemates route (tenant only, auth required)
      {
        path: '/housemates',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <HousematesHub />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      // Your housemate profile: the quiz (first run, `?quiz=1`) and every
      // section in one place. Static, so it ranks above /housemates/:id.
      {
        path: '/housemates/me',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <MyHousemateProfile />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      // One way to start a household (`?with=<userId>` from a mutual
      // Interested, `?lease=<agreementId>` from Find my replacement).
      {
        path: '/housemates/start',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <StartHousehold />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      // "List our group" became Start a household.
      {
        path: '/housemates/list-group',
        element: <Navigate to="/housemates/start" replace />,
      },
      // One housemate profile as a page (shareable, reopenable; noindex
      // under the /housemates prefix).
      {
        path: '/housemates/:id',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <HousemateProfilePage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },

      // Profile routes (auth required)
      {
        path: '/profile',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <ProfileView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/profile/screening',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <MyScreening />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/profile/screening/get',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <SelfScreeningOrder />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/screening/consent/:applicationId',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <ScreeningConsent />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/profile/edit',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <EditProfile />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/profile/tenant-dashboard',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <StudentTenantDashboard />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },

      // Payment routes (auth required)
      {
        path: '/payments',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <PaymentsView />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      // Pre-qualification (tenant only, auth required)
      {
        path: '/pre-qualify',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <PreQualificationFlow />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },

      // Application routes (auth required)
      {
        path: '/applications',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <MyApplications />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      // The one standard rental application, kept on the account.
      {
        path: '/applications/rental-application',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <RentalApplicationPage />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/apply/:listingId',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <ApplicationFlow />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/agreement/:agreementId',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <AgreementView />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // The household's shared inspection record: any tenant on the lease edits it.
      {
        path: '/inspections/:id/edit',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <InspectionEditor mode="tenant" />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      // Read-only inspection report for anyone on the lease (tenant or
      // landlord); reached from the completion email.
      {
        path: '/inspections/:id',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<SuspenseFallback />}>
              <InspectionReport />
            </Suspense>
          </ProtectedRoute>
        ),
      },

      // Legacy roommate routes — superseded by the real Housemates feature.
      // Redirect any stray links/bookmarks there.
      {
        path: '/roommates',
        element: <Navigate to="/housemates" replace />,
      },
      {
        path: '/roommates/matching',
        element: <Navigate to="/housemates" replace />,
      },

      // Groups merged into Housemates (2026-10-26): the old addresses
      // redirect to Housemates and the one household page.
      {
        path: '/groups',
        element: <Navigate to="/housemates" replace />,
      },
      {
        path: '/groups/create',
        element: <Navigate to="/housemates/start" replace />,
      },
      {
        path: '/groups/:id',
        element: <GroupRedirect />,
      },
      {
        path: '/groups/:id/chat',
        element: <GroupRedirect suffix="/chat" />,
      },
      // A household: members manage it here; others see it when it is
      // listed. Signed out, a shared link shows the public card at most.
      // Noindexed (the /households prefix).
      {
        path: '/households/:id',
        element: <HouseholdRoute />,
      },
      // The household's one chat (members only).
      {
        path: '/households/:id/chat',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['student']}>
              <Suspense fallback={<SuspenseFallback />}>
                <GroupChat />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },

      // Landlord/Owner routes (auth required)
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['owner']}>
              <OwnerToolsLayout>
                <Suspense fallback={<SuspenseFallback />}>
                  <OwnerDashboard />
                </Suspense>
              </OwnerToolsLayout>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      {
        // Google OAuth redirect (GOOGLE_OAUTH_REDIRECT_URI on myrentra.com).
        path: '/calendar/google/callback',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['owner']}>
              <Suspense fallback={<SuspenseFallback />}>
                <GoogleCalendarCallback />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/inbox',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['owner']}>
              <Suspense fallback={<SuspenseFallback />}>
                <LandlordInbox />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/listings/new',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['owner']}>
              <Suspense fallback={<SuspenseFallback />}>
                <LandlordListingForm />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      {
        path: '/dashboard/listings/:id/edit',
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['owner']}>
              <Suspense fallback={<SuspenseFallback />}>
                <LandlordListingForm />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      },
      // Live owner tools (wired to real APIs).
      ...[
        // "I have tenants already": a static segment, ranked above :id.
        {
          path: '/dashboard/properties/new',
          Component: RentedPropertyForm,
        },
        { path: '/dashboard/properties/:id', Component: PropertyWorkspace },
        {
          path: '/dashboard/properties/:id/onboard',
          Component: OnboardTenantsFlow,
        },
        // Five segments, so it never collides with the four-segment :tab route.
        {
          path: '/dashboard/properties/:id/inspections/:inspectionId',
          Component: InspectionEditor,
        },
        {
          path: '/dashboard/properties/:id/:tab',
          Component: PropertyWorkspace,
        },
        { path: '/dashboard/import', Component: ImportProperties },
        {
          path: '/dashboard/rent-collection',
          withTools: true,
          Component: RentCollection,
        },
        {
          path: '/dashboard/security-deposits',
          withTools: true,
          Component: SecurityDeposits,
        },
        {
          path: '/dashboard/payments-in',
          withTools: true,
          Component: PaymentsIn,
        },
        { path: '/dashboard/banking', withTools: true, Component: Bookkeeping },
        { path: '/dashboard/tax', withTools: true, Component: TaxCenter },
        {
          path: '/dashboard/documents',
          withTools: true,
          Component: OwnerDocuments,
        },
        { path: '/dashboard/team', Component: OwnerTeam },
        {
          path: '/dashboard/reports',
          withTools: true,
          Component: OwnerReports,
        },
        {
          path: '/dashboard/showings',
          withTools: true,
          Component: OwnerShowings,
        },
        {
          path: '/dashboard/automation',
          withTools: true,
          Component: OwnerAutomation,
        },
        {
          path: '/dashboard/vendors',
          withTools: true,
          Component: OwnerVendors,
        },
        {
          path: '/dashboard/inspections',
          withTools: true,
          Component: OwnerInspections,
        },
        {
          path: '/dashboard/lease-templates',
          withTools: true,
          Component: LeaseTemplates,
        },
        {
          path: '/dashboard/lease-templates/:id',
          Component: LeaseTemplateEditor,
        },
        {
          path: '/dashboard/screening/:applicationId',
          Component: LandlordScreening,
        },
        {
          path: '/dashboard/applications/:id/application',
          Component: ApplicationRecord,
        },
      ].map(({ path, Component, withTools }) => {
        const page = (
          <Suspense fallback={<SuspenseFallback />}>
            <Component />
          </Suspense>
        )
        return {
          path,
          element: (
            <ProtectedRoute>
              <RoleRoute allowedRoles={['owner']}>
                {withTools ? <OwnerToolsLayout>{page}</OwnerToolsLayout> : page}
              </RoleRoute>
            </ProtectedRoute>
          ),
        }
      }),
      // The Inbox is the approvals workspace; old links land there.
      {
        path: '/dashboard/approvals',
        element: <Navigate to="/dashboard/inbox" replace />,
      },
      // Gated owner screens (P0-3): honest Coming Soon instead of mock data.
      ...[
        {
          path: '/dashboard/disputes',
          title: 'Dispute Resolution',
          description: 'Structured dispute and mediation tools are planned.',
        },
      ].map(({ path, title, description }) => ({
        path,
        element: (
          <ProtectedRoute>
            <RoleRoute allowedRoles={['owner']}>
              <Suspense fallback={<SuspenseFallback />}>
                <ComingSoon title={title} description={description} />
              </Suspense>
            </RoleRoute>
          </ProtectedRoute>
        ),
      })),
      // Any other path: an honest "Page not found" (noindex; the Vercel
      // middleware also answers it with a 404 status).
      {
        path: '*',
        element: (
          <Suspense fallback={<SuspenseFallback />}>
            <NotFound />
          </Suspense>
        ),
      },
    ],
  },
]

/**
 * Every route sits under RouteHead, the one place that sets the canonical
 * URL and marks private areas noindex (seo/routes.js).
 */
const rootRoutes = [{ element: <RouteHead />, children: routeConfig }]

/** The route object for one declared path (tests render its element). */
export function routeFor(path, routes = routeConfig) {
  for (const route of routes) {
    if (route.path === path) return route
    const inner = route.children ? routeFor(path, route.children) : null
    if (inner) return inner
  }
  return null
}

/** Route paths the router declares, flattened (for seo/routes.test.js). */
export function declaredPaths(routes = routeConfig) {
  return routes.flatMap(route => [
    ...(route.path ? [route.path] : []),
    ...(route.children ? declaredPaths(route.children) : []),
  ])
}

/**
 * Create the router instance
 */
const router = createBrowserRouter(rootRoutes)

/**
 * Router provider component
 */
export default function AppRouter() {
  return <RouterProvider router={router} />
}

// Export route helpers for programmatic navigation
export { ProtectedRoute, RoleRoute, PublicRoute }
